import * as React from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";

/**
 * ComboboxInput - A combobox that allows both selection from a list AND free text input
 * 
 * @param {Object} props
 * @param {string} props.value - Current value
 * @param {function} props.onValueChange - Callback when value changes
 * @param {Array<string>} props.options - List of available options
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.emptyText - Text shown when no options match
 * @param {boolean} props.allowCustom - Whether to allow custom values (default: true)
 * @param {boolean} props.clearable - Whether to allow clearing the value (default: true)
 */
export function ComboboxInput({
  value,
  onValueChange,
  options = [],
  placeholder = "Sélectionner...",
  emptyText = "Aucun résultat.",
  allowCustom = true,
  clearable = true,
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  // Filter options based on input
  const filteredOptions = React.useMemo(() => {
    if (!inputValue) return options;
    const search = inputValue.toLowerCase();
    return options.filter((option) =>
      option.toLowerCase().includes(search)
    );
  }, [options, inputValue]);

  // Check if current input is a new value (not in options)
  const isNewValue = React.useMemo(() => {
    if (!inputValue) return false;
    return !options.some(
      (option) => option.toLowerCase() === inputValue.toLowerCase()
    );
  }, [options, inputValue]);

  const handleSelect = (selectedValue) => {
    onValueChange(selectedValue);
    setInputValue("");
    setOpen(false);
  };

  const handleAddNew = () => {
    if (inputValue.trim()) {
      onValueChange(inputValue.trim());
      setInputValue("");
      setOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onValueChange("");
    setInputValue("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal overflow-hidden",
            !value && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate flex-1 text-left">
            {value || placeholder}
          </span>
          <div className="flex items-center gap-1 ml-2">
            {clearable && value && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
                className="h-4 w-4 shrink-0 rounded-full hover:bg-gray-200 flex items-center justify-center cursor-pointer"
                title="Effacer"
              >
                <X className="h-3 w-3 opacity-70 hover:opacity-100" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Rechercher ou saisir...`}
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            {/* Option to clear value */}
            {clearable && value && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => handleSelect("")}
                  className="text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  Effacer la sélection
                </CommandItem>
              </CommandGroup>
            )}

            {filteredOptions.length === 0 && !isNewValue && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            
            {/* Option to add new value */}
            {allowCustom && isNewValue && inputValue.trim() && (
              <CommandGroup heading="Nouvelle entrée">
                <CommandItem
                  onSelect={handleAddNew}
                  className="text-primary"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter "{inputValue.trim()}"
                </CommandItem>
              </CommandGroup>
            )}

            {/* Existing options */}
            {filteredOptions.length > 0 && (
              <CommandGroup heading="Options existantes">
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => handleSelect(option)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {option}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
